import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Hand, Activity, Brain, Trophy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ICONS = { Hand, Activity, Brain };

export default function StagiaireCursusList() {
    const [cursusProgress, setCursusProgress] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/stagiaire/cursus")
            .then((r) => setCursusProgress(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-stagiaire-cursus-list">
            <div>
                <div className="text-xs uppercase tracking-[0.25em] text-brand-orange">Mes cursus</div>
                <h1 className="mt-3 font-display text-5xl font-normal tracking-tightest">Votre progression.</h1>
                <p className="mt-2 max-w-xl text-brand-smoke">
                    Suivez l'avancement de chaque cursus de Prévention Active où vous êtes engagé.
                </p>
            </div>

            {loading ? (
                <div className="mt-10 text-brand-smoke">Chargement…</div>
            ) : cursusProgress.length === 0 ? (
                <div className="mt-10 rounded-3xl border border-dashed border-brand-line bg-white p-12 text-center" data-testid="empty-cursus">
                    <p className="text-brand-smoke">Aucune inscription pour l'instant.</p>
                    <Link to="/expertise-pa" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white">
                        Découvrir nos cursus <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            ) : (
                <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {cursusProgress.map((c) => {
                        const Icon = ICONS[c.icon] || Hand;
                        const pct = c.total > 0 ? Math.round((c.completed / c.total) * 100) : 0;
                        return (
                            <Link
                                key={c.slug}
                                to={`/stagiaire/cursus/${c.slug}`}
                                data-testid={`stagiaire-cursus-card-${c.slug}`}
                                className="group block overflow-hidden rounded-3xl border border-brand-line bg-white shadow-premium transition-all hover:-translate-y-0.5 hover:shadow-premium-lg"
                            >
                                <div className="relative h-44 overflow-hidden">
                                    <img src={c.image_url} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                    <div className="absolute left-5 top-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    {c.is_complete && (
                                        <span className="absolute right-5 top-5 inline-flex items-center gap-1 rounded-full bg-brand-orange px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                                            <Trophy className="h-3 w-3" /> Complet
                                        </span>
                                    )}
                                </div>
                                <div className="p-7">
                                    <h3 className="font-display text-2xl text-brand-ink">{c.name.replace("Cursus ", "")}</h3>
                                    <p className="mt-1 text-xs italic text-brand-orange">{c.tagline}</p>
                                    <div className="mt-5 grid grid-cols-3 gap-1.5">
                                        {c.formations.map((seg) => {
                                            let cls = "h-2 rounded-full ";
                                            if (seg.state === "completed") cls += "bg-brand-orange";
                                            else if (seg.state === "upcoming") cls += "bg-brand-orange/40";
                                            else cls += "bg-brand-line";
                                            return <div key={seg.slug} className={cls} />;
                                        })}
                                    </div>
                                    <div className="mt-3 text-sm text-brand-ink/70">
                                        <strong className="text-brand-orange">{pct}%</strong> · {c.completed}/{c.total} formations complétées
                                    </div>
                                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-ink group-hover:text-brand-orange">
                                        Voir le détail <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
